import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { MrpProfile } from '../models/mrp-profile.model';
import { Branch } from '../models/branch.model';
import { ExecutionLog } from '../models/execution-log.model';
import { DashboardKpis } from '../models/dashboard-kpis.model';
import { ExecutionDetails } from '../models/protheus-details.model';
import { DashboardResponse } from '../models/dashboard-response.model';
import { ProductsSummary } from '../models/products-summary.model';
import { PurchaseRequest } from '../models/purchase-request.model';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class MrpService {

    // --- DATA ---

    private http = inject(HttpClient);
    private readonly apiUrl = environment.apiUrl;

    branches = signal<Branch[]>([]);

    isLoading = signal<boolean>(false);
    lastRefreshedAt = signal<Date>(new Date());
    threadCount = signal<number>(4);
    pageSize = signal<number>(50);
    currentPage = signal<number>(1);
    totalItems = signal<number>(0);
    totalPages = signal<number>(1);

    // --- FILTERS & SORT STATE ---
    private columnFilters = signal<Record<string, string>>({});
    private sortColumn = signal<string | null>('startDate');
    private sortDirection = signal<'asc' | 'desc' | null>('desc');

    private readonly columnToOrderBy: Record<string, string> = {
        'id': 'Id',
        'startDate': 'CreatedAt',
        'startedAt': 'StartedAt',
        'finishedAt': 'FinishedAt',
        'executionTime': 'TimeElapsed',
        'status': 'Status',
        'externalReference': 'ExternalReference'
    };

    private readonly statusFrontendToBackend: Record<string, string> = {
        'Pending': 'Pending',
        'Processing': 'Processing',
        'Completed': 'Completed',
        'DocumentsGenerated': 'DocumentsGenerated',
        'Error': 'Error',
        'Cancelled': 'Cancelled',
        'Reserved': 'Reserved',
        'DocumentFailed': 'DocumentFailed',
        'Cancelling': 'Cancelling',
        'Deleted': 'Deleted'
    };

    updateThreadCount(count: number) {
        this.threadCount.set(count);
    }

    constructor() {
        this.loadAllData();
    }

    private loadAllData() {
        this.isLoading.set(true);
        this.loadBranches();
        this.loadProfiles();
        this.loadDashboardData();
    }

    private loadBranches() {
        this.http.get<any[]>(`${this.apiUrl}/Branch`).subscribe({
            next: (data: any[]) => {
                const mapped: Branch[] = data.map((item: any) => ({
                    id: item.uuid,
                    code: item.code,
                    name: item.name,
                    state: item.state,
                    calculateMrp: !!item.calculateMrp
                }));
                // Ordenar: filiais com calculateMrp=true primeiro
                mapped.sort((a, b) => (a.calculateMrp === b.calculateMrp ? 0 : a.calculateMrp ? -1 : 1));
                this.branches.set(mapped);
                // We don't strictly turn off here to avoid early dismissal if others running, 
                // but let's be consistent. Or rely on others. 
                // Actually, let's NOT turn off here to avoid race condition ending it too fast.
                // Re-reading: "toda vez que eu clicar em um perfil..." -> profile updates are critical.
                // "monitoramento" -> logs are critical.
                // Branches are auxiliary.
            },
            error: (err: any) => console.error('Erro ao carregar filiais', err)
        });
    }

    private loadDashboardData(silent = false) {
        if (!silent) this.isLoading.set(true);
        const url = this.buildDashboardUrl();
        this.http.get<DashboardResponse>(url).subscribe({
            next: (data: DashboardResponse) => {
                // --- Map KPIs ---
                const kpiMap: Record<string, string> = {};
                (data.kpis || []).forEach(k => kpiMap[k.type] = k.value);
                this.kpis.set({
                    processed: Number(kpiMap['ExecutionsProcessed'] || 0),
                    processing: Number(kpiMap['ExecutionsProcessing'] || 0),
                    errors: Number(kpiMap['ExecutionsFailed'] || 0),
                    queue: Number(kpiMap['ExecutionsPending'] || 0),
                    avgTimeStr: kpiMap['ExecutionsAverageTime'] || '00m 00s'
                });

                // --- Map Executions ---
                const items = data.executions?.items || [];
                const mapped = items.filter((i: any) => i).map((item: any) => {
                    const statusRaw = item.status || '';

                    return {
                        id: item.uuid,
                        externalReference: item.externalReference || undefined,
                        startDate: new Date(item.createdAt),
                        startedAt: item.startedAt || undefined,
                        finishedAt: item.finishedAt || undefined,
                        executionTime: item.timeElapsed || '-',
                        profileName: item.calculationProfileName,
                        branchCode: item.branch?.code || 'N/A',
                        branchName: item.branch?.name || 'N/A',
                        status: statusRaw as any,
                        message: item.message
                    };
                });
                this.executionLogs.set(mapped);
                this.rawExecutionItems.set(items);

                // --- Pagination metadata ---
                this.totalItems.set(data.executions?.itemsAmount ?? mapped.length);
                this.totalPages.set(data.executions?.pageCount ?? 1);

                if (!silent) this.isLoading.set(false);
                this.lastRefreshedAt.set(new Date());
            },
            error: (err: any) => {
                console.error('Erro ao carregar dashboard', err);
                if (!silent) this.isLoading.set(false);
            }
        });
    }

    refreshMonitorData() {
        this.loadDashboardData(true);
    }

    refreshMonitorDataWithLoading() {
        this.isLoading.set(true);
        this.loadDashboardData();
    }

    changePageSize(size: number) {
        this.pageSize.set(size);
        this.currentPage.set(1);
        this.loadDashboardData();
    }

    goToPage(page: number) {
        if (page < 1 || page > this.totalPages()) return;
        this.currentPage.set(page);
        this.loadDashboardData();
    }

    nextPage() {
        this.goToPage(this.currentPage() + 1);
    }

    prevPage() {
        this.goToPage(this.currentPage() - 1);
    }

    // --- FILTER & SORT API ---

    applyFiltersAndSort(
        textFilters: Record<string, string>,
        sortCol: string | null,
        sortDir: 'asc' | 'desc' | null,
        structured?: { branchUuids?: string[]; profileUuids?: string[]; statuses?: string[]; dateStart?: string; dateEnd?: string; startedAtStart?: string; startedAtEnd?: string; finishedAtStart?: string; finishedAtEnd?: string }
    ) {
        this.columnFilters.set(textFilters);
        this.sortColumn.set(sortCol);
        this.sortDirection.set(sortDir);
        if (structured) this.structuredFilters.set(structured);
        this.currentPage.set(1);
        this.loadDashboardData();
    }

    clearAllFilters() {
        this.columnFilters.set({});
        this.structuredFilters.set({});
        this.currentPage.set(1);
        this.loadDashboardData();
    }

    private structuredFilters = signal<{ branchUuids?: string[]; profileUuids?: string[]; statuses?: string[]; dateStart?: string; dateEnd?: string; startedAtStart?: string; startedAtEnd?: string; finishedAtStart?: string; finishedAtEnd?: string }>({});

    private buildDashboardUrl(): string {
        const params: string[] = [];
        params.push(`Page=${this.currentPage()}`);
        params.push(`PageSize=${this.pageSize()}`);

        const filters = this.columnFilters();
        const sf = this.structuredFilters();

        // ExternalReference (from 'id' column filter — ticket)
        if (filters['id']) {
            params.push(`ExternalReference=${encodeURIComponent(filters['id'])}`);
        }

        // Statuses — structured (from checkbox list)
        if (sf.statuses && sf.statuses.length > 0) {
            sf.statuses.forEach(s => params.push(`Statuses=${s}`));
        }

        // Branches — structured UUIDs (from checkbox list)
        if (sf.branchUuids && sf.branchUuids.length > 0) {
            sf.branchUuids.forEach(id => params.push(`Branches=${id}`));
        }

        // Profiles — structured UUIDs (from checkbox list)
        if (sf.profileUuids && sf.profileUuids.length > 0) {
            sf.profileUuids.forEach(id => params.push(`CalculationProfiles=${id}`));
        }

        // Date range — converter BRT (-3) para UTC
        if (sf.dateStart) {
            // 00:00:00 BRT = 03:00:00 UTC do mesmo dia
            params.push(`CreatedAtStart=${sf.dateStart}T03:00:00Z`);
        }
        if (sf.dateEnd) {
            // 23:59:59 BRT = dia seguinte 02:59:59 UTC
            const endDate = new Date(sf.dateEnd + 'T00:00:00');
            endDate.setDate(endDate.getDate() + 1);
            const nextDay = endDate.toISOString().split('T')[0];
            params.push(`CreatedAtEnd=${nextDay}T02:59:59Z`);
        }

        // StartedAt date range
        if (sf.startedAtStart) {
            params.push(`StartedAtStart=${sf.startedAtStart}T03:00:00Z`);
        }
        if (sf.startedAtEnd) {
            const endDate = new Date(sf.startedAtEnd + 'T00:00:00');
            endDate.setDate(endDate.getDate() + 1);
            const nextDay = endDate.toISOString().split('T')[0];
            params.push(`StartedAtEnd=${nextDay}T02:59:59Z`);
        }

        // FinishedAt date range
        if (sf.finishedAtStart) {
            params.push(`FinishedAtStart=${sf.finishedAtStart}T03:00:00Z`);
        }
        if (sf.finishedAtEnd) {
            const endDate = new Date(sf.finishedAtEnd + 'T00:00:00');
            endDate.setDate(endDate.getDate() + 1);
            const nextDay = endDate.toISOString().split('T')[0];
            params.push(`FinishedAtEnd=${nextDay}T02:59:59Z`);
        }

        // OrderBy & OrderDirection
        const sortCol = this.sortColumn();
        const sortDir = this.sortDirection();
        if (sortCol && sortDir) {
            const apiField = this.columnToOrderBy[sortCol];
            if (apiField) {
                params.push(`OrderBy=${apiField}`);
                params.push(`OrderDirection=${sortDir === 'asc' ? 'Asc' : 'Desc'}`);
            }
        }

        return `${this.apiUrl}/Dashboard?${params.join('&')}`;
    }

    private autoRefreshInterval: any;

    startAutoRefresh() {
        if (this.autoRefreshInterval) return;
        this.autoRefreshInterval = setInterval(() => {
            console.log('Auto-refreshing monitor data...');
            this.refreshMonitorData();
        }, 120000); // 2 minutes
    }

    stopAutoRefresh() {
        if (this.autoRefreshInterval) {
            clearInterval(this.autoRefreshInterval);
            this.autoRefreshInterval = null;
        }
    }

    executionLogs = signal<ExecutionLog[]>([]);
    private rawExecutionItems = signal<any[]>([]);
    kpis = signal<DashboardKpis>({ processed: 0, processing: 0, errors: 0, queue: 0, avgTimeStr: '00m 00s' });

    profiles = signal<MrpProfile[]>([]);



    private loadProfiles() {
        this.http.get<any[]>(`${this.apiUrl}/CalculationProfile`).subscribe({
            next: (data: any[]) => {
                const mapped = data.map(item => this.mapToProfile(item));
                this.profiles.set(mapped);
                this.isLoading.set(false);
            },
            error: (err: any) => {
                console.error('Erro ao carregar perfis', err);
                this.isLoading.set(false);
            }
        });
    }

    private mapToProfile(item: any): MrpProfile {
        return {
            id: item.uuid,
            name: item.name,
            TELA_1: { HW2_CODIGO: item.setupCode },

            allocationSuggestion: item.allocationSuggestion,
            blockedLot: item.blockedLot,
            consignedIn: item.consignedIn,
            consignedOut: item.consignedOut,
            consolidateProductionOrder: item.consolidateProductionOrder,
            consolidatePurchaseRequest: item.consolidatePurchaseRequest,
            demandEndDate: String(item.demandEndOffsetDays),
            firmHorizon: item.firmHorizon,
            leadTime: item.leadTime,
            numberOfPeriods: item.numberOfPeriods,
            orderPoint: item.orderPoint,
            periodType: item.periodType,
            productionOrderNumber: item.productionOrderNumber,
            productionOrderType: item.productionOrderType,
            purchaseRequestNumber: item.purchaseRequestNumber,
            rejectedQuality: item.rejectedQuality,
            safetyStock: item.safetyStock,

            supplierCode: item.supplierCode || '',
            supplierStore: item.supplierStore || '',

            demandsProcessed: item.demandsProcessed,
            eventLog: item.eventLog,
            lGeraDoc: item.generatesDocument,
            lRastreiaEntradas: item.trackEntries,
            lastDemandImportAt: item.lastDemandImportAt || undefined,

            demandCodes: { LISTA: item.demandCodesList || '' },
            documents: { LISTA: item.documentsList || '' },
            productGroups: { LISTA: item.productGroupsList || '' },
            products: { LISTA: item.productsList || '' },
            productTypes: { LISTA: item.productTypesList || '' },

            demandType: {
                EMP_PROJETO: item.demandTypeEmpProjeto,
                MANUAL: item.demandTypeManual,
                PEDIDO_VENDA: item.demandTypePedidoVenda,
                PLANO_MESTRE: item.demandTypePlanoMestre,
                PREV_VENDAS: item.demandTypePrevVendas
            },
            documentType: {
                PREVISTOS: item.documentTypePrevistos,
                SACRAMENTADOS: item.documentTypeSacramentados,
                SUSPENSOS: item.documentTypeSuspensos
            }
        };
    }

    // kpis agora é um signal definido acima, populado por loadDashboardData()

    // deleted constructor

    addExecutionLog(log: ExecutionLog) {
        this.executionLogs.update(logs => [log, ...logs]);
    }

    createExecutionBatch(profileIds: string[], branchIds: string[]) {
        this.isLoading.set(true);
        const payload = {
            branchUuids: branchIds,
            calculationProfileUuids: profileIds
        };

        this.http.post(`${this.apiUrl}/Execution`, payload).subscribe({
            next: () => {
                console.log('Lote de execução criado com sucesso.');
                // Recarrega a lista para mostrar status AGUARDANDO (Pending)
                this.loadDashboardData();
            },
            error: (err) => {
                console.error('Erro ao criar lote de execução', err);
                this.isLoading.set(false);
            }
        });
    }

    updateExecutionLogStatus(id: string, status: any, startDate?: Date) {
        this.executionLogs.update(logs =>
            logs.map(log => log.id === id ? { ...log, status: status, startDate: startDate || log.startDate } : log)
        );
    }

    createProfile(profile: MrpProfile) {
        this.isLoading.set(true);
        const payload = {
            name: String(profile.name || ''),
            setupCode: String(profile.TELA_1?.HW2_CODIGO || ''),
            allocationSuggestion: String(profile.allocationSuggestion || ''),
            blockedLot: String(profile.blockedLot || ''),
            consignedIn: String(profile.consignedIn || ''),
            consignedOut: String(profile.consignedOut || ''),
            consolidateProductionOrder: String(profile.consolidateProductionOrder || ''),
            consolidatePurchaseRequest: String(profile.consolidatePurchaseRequest || ''),
            demandEndOffsetDays: Number(profile.demandEndDate) || 0,
            demandsProcessed: Boolean(profile.demandsProcessed),

            demandTypeEmpProjeto: Boolean(profile.demandType?.EMP_PROJETO),
            demandTypeManual: Boolean(profile.demandType?.MANUAL),
            demandTypePedidoVenda: Boolean(profile.demandType?.PEDIDO_VENDA),
            demandTypePlanoMestre: Boolean(profile.demandType?.PLANO_MESTRE),
            demandTypePrevVendas: Boolean(profile.demandType?.PREV_VENDAS),

            documentsList: String(profile.documents?.LISTA || ''),
            documentTypePrevistos: String(profile.documentType?.PREVISTOS || ''),
            documentTypeSacramentados: Boolean(profile.documentType?.SACRAMENTADOS),
            documentTypeSuspensos: Boolean(profile.documentType?.SUSPENSOS),

            eventLog: Boolean(profile.eventLog),
            firmHorizon: Number(profile.firmHorizon) || 0,
            leadTime: String(profile.leadTime || ''),
            generatesDocument: Boolean(profile.lGeraDoc),
            trackEntries: Boolean(profile.lRastreiaEntradas),

            numberOfPeriods: String(profile.numberOfPeriods || ''),
            orderPoint: String(profile.orderPoint || ''),
            periodType: String(profile.periodType || ''),
            productGroupsList: String(profile.productGroups?.LISTA || ''),
            productTypesList: String(profile.productTypes?.LISTA || ''),
            productsList: String(profile.products?.LISTA || ''),
            demandCodesList: String(profile.demandCodes?.LISTA || ''),

            productionOrderNumber: String(profile.productionOrderNumber || ''),
            productionOrderType: String(profile.productionOrderType || ''),
            purchaseRequestNumber: String(profile.purchaseRequestNumber || ''),
            rejectedQuality: String(profile.rejectedQuality || ''),
            safetyStock: String(profile.safetyStock || ''),

            supplierCode: String(profile.supplierCode || ''),
            supplierStore: String(profile.supplierStore || '')
        };

        this.http.post(`${this.apiUrl}/CalculationProfile`, payload).subscribe({
            next: (data: any) => {
                console.log('Perfil criado com sucesso.');
                setTimeout(() => this.loadProfiles(), 1000);
            },
            error: (err) => {
                console.error('Erro ao criar perfil', err);
                this.isLoading.set(false);
            }
        });
    }

    updateProfile(profile: MrpProfile) {
        this.isLoading.set(true);
        const payload = {
            name: String(profile.name || ''),
            setupCode: String(profile.TELA_1?.HW2_CODIGO || ''),
            allocationSuggestion: String(profile.allocationSuggestion || ''),
            blockedLot: String(profile.blockedLot || ''),
            consignedIn: String(profile.consignedIn || ''),
            consignedOut: String(profile.consignedOut || ''),
            consolidateProductionOrder: String(profile.consolidateProductionOrder || ''),
            consolidatePurchaseRequest: String(profile.consolidatePurchaseRequest || ''),
            demandEndOffsetDays: Number(profile.demandEndDate) || 0,
            demandsProcessed: Boolean(profile.demandsProcessed),

            demandTypeEmpProjeto: Boolean(profile.demandType?.EMP_PROJETO),
            demandTypeManual: Boolean(profile.demandType?.MANUAL),
            demandTypePedidoVenda: Boolean(profile.demandType?.PEDIDO_VENDA),
            demandTypePlanoMestre: Boolean(profile.demandType?.PLANO_MESTRE),
            demandTypePrevVendas: Boolean(profile.demandType?.PREV_VENDAS),

            documentsList: String(profile.documents?.LISTA || ''),
            documentTypePrevistos: String(profile.documentType?.PREVISTOS || ''),
            documentTypeSacramentados: Boolean(profile.documentType?.SACRAMENTADOS),
            documentTypeSuspensos: Boolean(profile.documentType?.SUSPENSOS),

            eventLog: Boolean(profile.eventLog),
            firmHorizon: Number(profile.firmHorizon) || 0,
            leadTime: String(profile.leadTime || ''),
            generatesDocument: Boolean(profile.lGeraDoc),
            trackEntries: Boolean(profile.lRastreiaEntradas),

            numberOfPeriods: String(profile.numberOfPeriods || ''),
            orderPoint: String(profile.orderPoint || ''),
            periodType: String(profile.periodType || ''),
            productGroupsList: String(profile.productGroups?.LISTA || ''),
            productTypesList: String(profile.productTypes?.LISTA || ''),
            productsList: String(profile.products?.LISTA || ''),
            demandCodesList: String(profile.demandCodes?.LISTA || ''),

            productionOrderNumber: String(profile.productionOrderNumber || ''),
            productionOrderType: String(profile.productionOrderType || ''),
            purchaseRequestNumber: String(profile.purchaseRequestNumber || ''),
            rejectedQuality: String(profile.rejectedQuality || ''),
            safetyStock: String(profile.safetyStock || ''),

            supplierCode: String(profile.supplierCode || ''),
            supplierStore: String(profile.supplierStore || '')
        };

        this.http.put(`${this.apiUrl}/CalculationProfile/${profile.id}`, payload).subscribe({
            next: (data: any) => {
                console.log('Perfil atualizado com sucesso.');
                setTimeout(() => this.loadProfiles(), 1000);
            },
            error: (err) => {
                console.error('Erro ao atualizar perfil', err);
                this.isLoading.set(false);
            }
        });
    }

    deleteProfile(id: string) {
        this.isLoading.set(true);
        this.http.delete(`${this.apiUrl}/CalculationProfile/${id}`).subscribe({
            next: () => {
                console.log('Perfil deletado com sucesso.');
                setTimeout(() => this.loadProfiles(), 1000);
            },
            error: (err) => {
                console.error('Erro ao deletar perfil', err);
                this.isLoading.set(false);
            }
        });
    }

    cancelExecution(id: string) {
        this.isLoading.set(true);
        this.http.post(`${this.apiUrl}/Execution/${id}/Cancel`, {}).subscribe({
            next: () => {
                console.log('Execução cancelada com sucesso.');
                this.loadDashboardData();
            },
            error: (err) => {
                console.error('Erro ao cancelar execução', err);
                this.isLoading.set(false);
            }
        });
    }

    retryExecution(id: string): Observable<any> {
        return this.http.post(`${this.apiUrl}/Execution/${id}/Retry`, {});
    }

    getUsers() {
        return this.http.get<any[]>(`${this.apiUrl}/User`);
    }

    getUserById(id: string) {
        return this.http.get<any>(`${this.apiUrl}/User/${id}`);
    }

    createUser(user: any) {
        return this.http.post(`${this.apiUrl}/User`, user);
    }

    updateUser(id: string, user: any) {
        return this.http.put(`${this.apiUrl}/User/${id}`, user);
    }

    deleteUser(id: string) {
        return this.http.delete(`${this.apiUrl}/User/${id}`);
    }

    // --- PARAMETERS ---

    getParameters() {
        return this.http.get<any[]>(`${this.apiUrl}/Parameter`);
    }

    updateParameters(params: { parameterKey: string, parameterValue: string }[]) {
        return this.http.patch(`${this.apiUrl}/Parameter/Value`, params);
    }

    // --- EXECUTION DETAILS (from Dashboard data) ---
    getExecutionDetails(id: string): ExecutionDetails | null {
        const item = this.rawExecutionItems().find((i: any) => i.uuid === id);
        if (!item) return null;
        return {
            uuid: item.uuid,
            branchCode: item.branch?.code || 'N/A',
            branchName: item.branch?.name || 'N/A',
            calculationProfileName: item.calculationProfileName || '',
            externalReference: item.externalReference,
            status: item.status,
            createdAt: item.createdAt,
            startedAt: item.startedAt,
            finishedAt: item.finishedAt,
            timeElapsed: item.timeElapsed,
            protheusMemoryLoadStatus: item.protheusMemoryLoadStatus,
            protheusLevelRecalculationStatus: item.protheusLevelRecalculationStatus,
            protheusCalculationStatus: item.protheusCalculationStatus,
            protheusPersistenceStatus: item.protheusPersistenceStatus,
            message: item.message
        };
    }

    // --- EXECUTION EXTENDED DETAILS ---
    getProductsSummary(executionId: string): Observable<ProductsSummary> {
        return this.http.get<ProductsSummary>(
            `${this.apiUrl}/Execution/${executionId}/Products/Summary`
        );
    }

    getPurchaseRequests(executionId: string): Observable<PurchaseRequest[]> {
        return this.http.get<PurchaseRequest[]>(
            `${this.apiUrl}/Execution/${executionId}/PurchaseRequests`
        );
    }
}
